
import Vue from 'vue';

import { categories } from '@root/modules/customer/config/customer.config';
import { GetCustomerData } from '@root/modules/customer/types/customer';
import { faCircleInfo } from '@delfi.lt/fa-kit/icons/modules/classic/regular';

interface Category {
  key: string;
  name: string;
  categories: number[];
}

interface Data {
  categories: Category[];
  selectedCategories: string[];
  categoriesCount: number;
  unsticked: boolean;
  stickyBottomPos: string;
  error: boolean;
}

interface Props {
  usersCategories: GetCustomerData['data']['data'] | null;
}

interface Computed {
  sortedCategories: Category[];
  stickyStyle: Record<string, any>;
}

interface Methods {
  setActiveInactive: (event: MouseEvent) => void;
  saveCategories: () => void;
  handleScroll: () => void;
  skipCategoriesSelect: () => void;
  isActiveCategory: (category: string) => boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  data() {
    return {
      categories,
      selectedCategories: [],
      categoriesCount: this.usersCategories?.items.length || 0,
      unsticked: false,
      stickyBottomPos: '68px',
      error: false,
      faCircleInfo,
    };
  },
  props: {
    usersCategories: {
      type: Object || null,
      default: null,
    },
  },
  methods: {
    setActiveInactive(event) {
      const target = event.target as HTMLElement;
      const category = target.dataset.key;

      if (category) {
        this.categoriesCount = this.isActiveCategory(category) ? this.categoriesCount - 1 : this.categoriesCount + 1;

        if (!this.selectedCategories.includes(category)) {
          this.selectedCategories.push(category);
        } else {
          this.selectedCategories = this.selectedCategories.filter((item) => item !== category);
        }
      }
    },
    isActiveCategory(categoryKey) {
      const isUserCategorySelected = this.usersCategories ? this.usersCategories.items.some((item) => item.value === categoryKey) : false;
      const isSelected = this.selectedCategories.includes(categoryKey);

      return isUserCategorySelected !== isSelected;
    },
    saveCategories() {
      if (this.categoriesCount >= 5) {
        this.$emit('save-categories', this.selectedCategories);
      } else {
        this.error = true;
      }
    },
    skipCategoriesSelect() {
      this.$event.send('link-click', { name: 'Skip', type: 'mydelfi_categories' });

      if (this.usersCategories && this.usersCategories.items.length > 0) {
        this.$emit('set-active-page', 'Headlines');
      } else {
        window.location.href = 'https://www.delfi.lt';
      }
    },
    handleScroll() {
      const sentinel = this.$refs.sentinel as Element;
      const observer = new IntersectionObserver(
        ([entry]) => {
          this.unsticked = entry.isIntersecting;
        },
        { root: null, threshold: 0, rootMargin: `0px 0px -68px 0px` }
      );
      if (sentinel) {
        observer.observe(sentinel);
      }
    },
  },
  computed: {
    sortedCategories() {
      if (!this.usersCategories) {
        return this.categories;
      }
      const categories = this.categories;

      categories.sort((a, b) => {
        const aInPriority = this.usersCategories?.items.some((item) => item.value === a.key);
        const bInPriority = this.usersCategories?.items.some((item) => item.value === b.key);

        if (aInPriority === bInPriority) {
          return 0;
        }

        return aInPriority ? -1 : 1;
      });

      return categories;
    },
    stickyStyle() {
      if (window && window.innerWidth > 768) return {};

      return {
        position: this.unsticked ? 'absolute' : 'fixed',
        bottom: this.unsticked ? '0px' : this.stickyBottomPos,
        top: this.unsticked ? 'unset' : 'auto',
      };
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
});
